/**
 * Workflow API
 *
 * Wraps backend workflow endpoints. The Athena backend exposes workflow
 * routes under TWO prefixes — most live at `/workflow/*` (no `/api/v1`
 * prefix), while a few (compliance-gap, attack-trees, dashboard,
 * residual-enhanced) live under `/api/v1/*`. The shared axios client
 * has baseURL `/api/v1`; calls under `/workflow/*` therefore override
 * baseURL to `''` per request, while calls under `/api/v1/*` use paths
 * relative to that prefix.
 *
 * Cross-origin call sites in the original code (those that prefixed
 * `${config.BACKEND_URL}` to be explicitly cross-origin in dev) are
 * preserved by passing absolute URLs — axios uses absolute URLs as-is.
 */

import client from './client';
import { config } from '../config/env';

const NO_API_V1 = { baseURL: '' };

// ── /workflow/* (no /api/v1 prefix) ─────────────────────────────────────────

export async function getWorkflowStatus(threadId, { signal } = {}) {
  return client.get(`/workflow/${threadId}/status`, { ...NO_API_V1, signal });
}

export async function getAgentResult(threadId, agentName, { signal } = {}) {
  return client.get(
    `/workflow/${threadId}/agent/${agentName}/result`,
    { ...NO_API_V1, signal }
  );
}

export async function saveAgentRecord(threadId, agentName, recordData) {
  return client.post(
    `/workflow/${threadId}/agent/${agentName}/record`,
    recordData,
    NO_API_V1
  );
}

/**
 * Fetch the structured item-definition for a thread. Preserves the
 * original cross-origin behaviour (callers used `${config.BACKEND_URL}`).
 */
export async function getItemDefinition(threadId) {
  const baseUrl = config.BACKEND_URL;
  return client.get(
    `${baseUrl}/workflow/${threadId}/item-definition/complete?format=structured`
  );
}

/**
 * Update the structured item-definition for a thread. Preserves the
 * original cross-origin behaviour.
 */
export async function updateItemDefinition(threadId, itemDefinition) {
  const baseUrl = config.BACKEND_URL;
  return client.put(
    `${baseUrl}/workflow/${threadId}/item-definition/update`,
    { item_definition: itemDefinition }
  );
}

/**
 * Submit a revision for an agent — POST /workflow/{tid}/revise.
 *
 * Backend route lives at the bare backend root (no /api/v1 prefix);
 * NO_API_V1 strips the shared client's default baseURL so the path
 * resolves correctly.
 *
 * **Long-poll timeout (5 min)**: in LEGACY mode the backend handler
 * awaits the full agent re-run before returning (it is NOT
 * fire-and-forget), so this call can legitimately take 1–4 minutes.
 * The shared client's default 30s timeout would 504 prematurely while
 * the backend is still working; bumping it to 300s here covers the
 * worst case (the legacy poll loop's 150 × 2s = 5min cap is the
 * matching legacy upper bound).
 *
 * **SQS execution mode** (FEATURE_FLAG_SQS_WORKFLOW_EXECUTION on the
 * backend): the handler instead ENQUEUES the revision and returns
 * immediately with `action_taken: 'revision_queued'`. Callers must
 * branch on that field — useReviseFlow bridges the queued case by
 * polling /status until the re-run visibly starts. The 300s timeout
 * stays for legacy-mode compatibility (harmless when the response is
 * instant).
 *
 * The `baseURL` parameter is a legacy artifact (the original caller
 * passed `backendService.baseURL` — the absolute backend root). It's
 * accepted and silently ignored for backwards compatibility; new
 * callers should omit it.
 */
const REVISE_TIMEOUT_MS = 300_000;

export async function reviseWorkflow({ threadId, feedback, targetAgent, signal = undefined }) {
  return client.post(
    `/workflow/${threadId}/revise`,
    { thread_id: threadId, feedback, target_agent: targetAgent },
    { ...NO_API_V1, signal, timeout: REVISE_TIMEOUT_MS }
  );
}

// ── v2 reviewer chat — additive, no /api/v1 prefix ──────────────────────────

/**
 * Fetch the chat thread for one agent slot of a workflow.
 * Returns { thread_id, agent_name, messages: ChatMessage[] }.
 * Empty messages array for workflows predating the v2 chat feature.
 */
export async function getChatMessages(threadId, agentName, { signal } = {}) {
  return client.get(
    `/workflow/${threadId}/chat`,
    { ...NO_API_V1, signal, params: { agent: agentName } }
  );
}

/**
 * Append a message to one agent slot's chat. Server stamps id +
 * timestamp; clients supply role + text + agent_name only.
 * Returns { message: ChatMessage } — the stamped message.
 */
export async function appendChatMessage(threadId, { agentName, role, text }, { signal } = {}) {
  return client.post(
    `/workflow/${threadId}/chat`,
    { agent_name: agentName, role, text },
    { ...NO_API_V1, signal }
  );
}

// ── /api/v1/* (under the default baseURL) ───────────────────────────────────

/**
 * Generate an attack tree for a specific threat. Original call used
 * `${config.BACKEND_URL}/api/v1/...` (cross-origin in dev) — preserve.
 */
export async function generateAttackTree(threadId, threatId) {
  const baseUrl = config.BACKEND_URL;
  return client.post(
    `${baseUrl}/api/v1/attack-trees/${threadId}/generate?threat_id=${encodeURIComponent(threatId)}`
  );
}

/**
 * Fetch the TARA dashboard (list of recent analyses).
 *
 * The original call was `${VITE_API_BASE_URL || '/api/v1'}/fetchTARADashboard`
 * — same effective URL as the default baseURL `/api/v1` + path
 * `/fetchTARADashboard`.
 */
export async function getTARADashboard() {
  return client.get('/fetchTARADashboard');
}

/**
 * Compute the compliance gap for a thread against a standard.
 * Preserves the original cross-origin behaviour.
 */
export async function evaluateComplianceGap(threadId, { standardId, overwrite = false }) {
  const baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8001';
  return client.get(
    `${baseUrl}/api/v1/workflow/${threadId}/compliance-gap?standard_id=${standardId}&overwrite=${overwrite}`
  );
}

/**
 * Poll the residual-enhanced TARA evidence-collection status for a session.
 */
export async function getResidualEvidenceStatus(sessionId) {
  return client.get(`/residual-enhanced/evidence-status/${sessionId}`);
}

// ── Workflow control: resume / complete / record CRUD ───────────────────────

/**
 * Resume the workflow at a specific agent with optional user feedback.
 * Mirrors AthenaBackendService.resumeWorkflow(agentName, context).
 *
 * The legacy method runs context → feedback through `generateFeedback()`
 * internally; the api wrapper takes the precomputed `userFeedback` string
 * (callers do that step). All four IDs (`threadId`, `sessionId`,
 * `tenantId`, `agentName`) are required by the backend.
 */
export async function resumeWorkflow({
  threadId,
  sessionId,
  tenantId,
  agentName,
  userFeedback = '',
}) {
  return client.post(
    `/workflow/resume`,
    {
      thread_id: threadId,
      agent_name: agentName,
      session_id: sessionId,
      tenant_id: tenantId,
      user_feedback: userFeedback,
    },
    NO_API_V1
  );
}

/**
 * Mark the workflow as complete (for the final-step approval flow).
 * Mirrors AthenaBackendService.completeWorkflow().
 */
export async function completeWorkflow(threadId, sessionId) {
  return client.post(
    `/workflow/complete`,
    { thread_id: threadId, session_id: sessionId },
    NO_API_V1
  );
}

/**
 * PATCH a single record on an agent's data.
 * Mirrors AthenaBackendService.updateSingleRecord(agentName, recordId, updates).
 */
export async function updateSingleRecord(threadId, agentName, recordId, updates) {
  return client.patch(
    `/workflow/${threadId}/agent/${agentName}/record/${recordId}`,
    { updates },
    NO_API_V1
  );
}

/**
 * Convenience: PATCH a single field on a record.
 * Mirrors AthenaBackendService.updateRecordField(agentName, recordId, fieldName, value).
 */
export async function updateRecordField(threadId, agentName, recordId, fieldName, value) {
  return updateSingleRecord(threadId, agentName, recordId, { [fieldName]: value });
}

/**
 * Approve hierarchical child data with relevance modifications.
 * Mirrors AthenaBackendService.approveHierarchicalData(agentName, approvedItems, modifications).
 */
export async function approveHierarchicalData(threadId, agentName, approvedItems, modifications) {
  return client.post(
    `/workflow/${threadId}/agent/${agentName}/approve-hierarchical`,
    { approved_items: approvedItems, modifications },
    NO_API_V1
  );
}

/**
 * Add a new record to an agent's data array. Wraps the body as `{ record }`.
 * Mirrors AthenaBackendService.addRecord(agentName, newRecord).
 *
 * Note: `saveAgentRecord` posts to the same URL but accepts an arbitrary
 * body — `addRecord` is the convenience wrapper that matches the legacy
 * shape `{ record: newRecord }`.
 */
export async function addRecord(threadId, agentName, newRecord) {
  return client.post(
    `/workflow/${threadId}/agent/${agentName}/record`,
    { record: newRecord },
    NO_API_V1
  );
}

/**
 * Delete a record from an agent's data array. Supports `cascade` and
 * `force` query flags. Mirrors AthenaBackendService.deleteRecord(
 * agentName, recordId, cascade, force).
 */
export async function deleteRecord(threadId, agentName, recordId, { cascade = false, force = false } = {}) {
  try {
    return await client.delete(
      `/workflow/${threadId}/agent/${agentName}/record/${recordId}?cascade=${cascade}&force=${force}`,
      NO_API_V1
    );
  } catch (err) {
    // Story 6.3 follow-up: preserve the legacy class's 409 translation.
    // Backend returns {detail: {message, dependent_records, warnings,
    // affected_count, record_id, record_type}}. Step components catch
    // `code === 'HAS_DEPENDENCIES'` to open the cascade-delete dialog —
    // without this translation the dialog never opens and the user sees
    // the same 409 on retry.
    if (err?.status === 409 && err?.detail) {
      const errorWithDetails = new Error(
        err.detail.message || 'This record has dependent records'
      );
      errorWithDetails.code = 'HAS_DEPENDENCIES';
      errorWithDetails.dependentRecords = err.detail.dependent_records || [];
      errorWithDetails.warnings = err.detail.warnings || [];
      errorWithDetails.affectedCount = err.detail.affected_count || 0;
      errorWithDetails.recordId = err.detail.record_id;
      errorWithDetails.recordType = err.detail.record_type;
      throw errorWithDetails;
    }
    throw err;
  }
}

// ── Workflow version control ────────────────────────────────────────────────

/**
 * List workflow versions, newest first. The snapshot payload is omitted
 * — fetch it via `getVersionDiff(threadId, n, { includeSnapshot: true })`.
 * Mirrors AthenaBackendService.getWorkflowVersions(threadId, opts).
 */
export async function getWorkflowVersions(threadId, { offset = 0, limit = 50 } = {}) {
  const qs = `?offset=${encodeURIComponent(offset)}&limit=${encodeURIComponent(limit)}`;
  return client.get(`/workflow/${threadId}/versions${qs}`, NO_API_V1);
}

/**
 * Get a single version's full diff. Pass `includeSnapshot: true` to
 * include the heavy snapshot payload.
 * Mirrors AthenaBackendService.getVersionDiff(threadId, versionNumber, opts).
 */
export async function getVersionDiff(threadId, versionNumber, { includeSnapshot = false } = {}) {
  const qs = includeSnapshot ? '?include_snapshot=true' : '';
  return client.get(
    `/workflow/${threadId}/versions/${versionNumber}${qs}`,
    NO_API_V1
  );
}

/**
 * Restore the workflow to a given version. Creates a NEW top-of-timeline
 * version of operation_type='restore' — forward versions are preserved.
 * Mirrors AthenaBackendService.restoreToVersion(threadId, versionNumber).
 */
export async function restoreToVersion(threadId, versionNumber) {
  return client.post(
    `/workflow/${threadId}/versions/${versionNumber}/restore`,
    {},
    NO_API_V1
  );
}

// ── Thread lifecycle ────────────────────────────────────────────────────────

/**
 * Delete a backend workflow thread. Mirrors the canonical leg of
 * AthenaBackendService.deleteThread(threadId).
 *
 * The legacy method falls back to `/workflow/{tid}/stop` when delete
 * returns 404 — callers that want that behaviour should call `stopThread`
 * explicitly on their own 404 catch.
 */
export async function deleteThread(threadId) {
  return client.delete(`/workflow/${threadId}/delete`, NO_API_V1);
}

/**
 * Stop a backend workflow thread without deleting it.
 * Mirrors the fallback leg of AthenaBackendService.deleteThread(threadId).
 */
export async function stopThread(threadId) {
  return client.post(`/workflow/${threadId}/stop`, undefined, NO_API_V1);
}

// ── Dashboard alias ─────────────────────────────────────────────────────────

/**
 * Alias of `getTARADashboard` to preserve 1:1 traceability with
 * `AthenaBackendService.getDashboardSummary(tenantId)`. The legacy
 * method passes `?tenant_id={tid}` in the query string; the backend
 * actually scopes by the JWT's `tid` claim, so the query parameter is
 * informational only and the alias drops it.
 */
export const getDashboardSummary = getTARADashboard;
